import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Simple feature tours (single page, no navigation)
const SIMPLE_TOURS = {
  canvases: [
    {
      target: "#tour-new-canvas-btn",
      placement: "bottom",
      title: "Create Your First Canvas",
      content: "Click here to start. The AI wizard will generate a full business model canvas from just a short description of your idea.",
    },
    {
      target: "#tour-canvas-grid",
      placement: "top",
      title: "Your Canvas Collection",
      content: "Your canvases appear here as cards showing hypothesis, persona, and document counts. Click any card to dive in.",
      optional: true,
    },
  ],

  hypothesis_board: [
    {
      target: "#tour-hypothesis-kanban",
      placement: "top",
      title: "Hypothesis Kanban",
      content: "Hypotheses flow through four stages: <strong>Pending</strong> → <strong>Testing</strong> → <strong>Validated</strong> → <strong>Rejected</strong>. Drag cards to update their status.",
    },
    {
      target: "#tour-hypothesis-canvas-filter",
      placement: "bottom",
      title: "Filter by Canvas",
      content: "Focus on one business model at a time by filtering hypotheses by canvas.",
      optional: true,
    },
    {
      target: "#tour-hypothesis-generate-btn",
      placement: "bottom",
      title: "Generate Hypotheses",
      content: "Automatically generate testable hypotheses from any of your canvases using AI.",
      optional: true,
    },
  ],

  journey_map_detail: [
    {
      target: "#tour-journey-grid",
      placement: "top",
      title: "Customer Journey Grid",
      content: "Columns are journey stages (Awareness → Advocacy). Rows capture actions, thoughts, emotions, and pain points at each stage.",
    },
    {
      target: "#tour-journey-add-controls",
      placement: "bottom",
      title: "Customize Your Map",
      content: "Add stages or rows to tailor the journey map to your specific customer experience.",
      optional: true,
    },
  ],

  roadmap_detail: [
    {
      target: "#tour-roadmap-columns",
      placement: "top",
      title: "Plan by Priority",
      content: "Organize features and initiatives into columns — typically Now, Next, and Later. Drag items to reprioritize.",
    },
    {
      target: "#tour-roadmap-add-column",
      placement: "bottom",
      title: "Add Columns",
      content: "Create custom columns to match your planning style — sprints, quarters, or themes.",
      optional: true,
    },
  ],

  tasks: [
    {
      target: "#tour-task-kanban",
      placement: "top",
      title: "Task Board",
      content: "Track work across <strong>To Do</strong>, <strong>In Progress</strong>, <strong>Review</strong>, and <strong>Done</strong>. Drag cards between columns to update status.",
    },
    {
      target: "#tour-task-filters",
      placement: "bottom",
      title: "Filter Tasks",
      content: "Narrow your view by roadmap or team member to focus on what matters right now.",
      optional: true,
    },
    {
      target: "#tour-new-task-btn",
      placement: "bottom",
      title: "Create Tasks",
      content: "Add tasks manually, or link them to roadmap items for traceability.",
      optional: true,
    },
  ],
}

// Multi-phase canvas detail tour — each phase maps to a tab
// Phases are identified by which tab is active (detected via URL or DOM)
const CANVAS_DETAIL_PHASES = [
  {
    id: "canvas",
    tabMatch: (path) => !path.includes("/hypotheses") && !path.includes("/personas") && !path.includes("/documents") && !path.includes("/insights"),
    steps: [
      {
        target: "#tour-canvas-grid-display",
        placement: "top",
        title: "Your Business Model",
        content: "Each block represents a section of your canvas — Problem, Solution, Channels, Revenue, and more. Click any card to edit it.",
      },
      {
        target: "#tour-canvas-publish-btn",
        placement: "bottom",
        title: "Share Your Canvas",
        content: "Publish your canvas to get a public link you can share with stakeholders, mentors, or investors. Also available as markdown for easy access by LLMs.",
        optional: true,
      },
    ],
    nextTab: "#tour-canvas-tab-hypotheses",
    nextLabel: "Next: Hypotheses →",
  },
  {
    id: "hypotheses",
    tabMatch: (path) => path.includes("/hypotheses"),
    steps: [
      {
        target: "#tour-canvas-generate-hypotheses",
        placement: "bottom",
        title: "Generate Hypotheses",
        content: "Click Generate to create testable assumptions from your canvas blocks, each scored by risk level.",
        optional: true,
      },
      {
        target: "#tour-canvas-interview-questions",
        placement: "bottom",
        title: "Interview Questions",
        content: "Generate customer interview questions based on your hypotheses — perfect for customer discovery calls.",
        optional: true,
      },
    ],
    nextTab: "#tour-canvas-tab-personas",
    nextLabel: "Next: Personas →",
  },
  {
    id: "personas",
    tabMatch: (path) => path.includes("/personas"),
    steps: [
      {
        target: "#tour-canvas-generate-personas",
        placement: "bottom",
        title: "Generate Personas",
        content: "Create detailed customer personas from your canvas's customer segment cards using AI.",
        optional: true,
      },
      {
        target: "#canvas-personas-board",
        placement: "top",
        title: "Persona Details & Document Tagging",
        content: "Select a persona to view their profile. Tag discovery documents to specific personas to build evidence for each customer segment.",
        optional: true,
      },
    ],
    nextTab: "#tour-canvas-tab-discoveries",
    nextLabel: "Next: Discoveries →",
  },
  {
    id: "documents",
    tabMatch: (path) => path.includes("/documents"),
    steps: [
      {
        target: "#tour-canvas-upload-discovery",
        placement: "bottom",
        title: "Upload Discoveries",
        content: "Upload interview transcripts, research documents, and market data. These feed into hypothesis validation and persona building.",
        optional: true,
      },
    ],
    nextTab: "#tour-canvas-tab-insights",
    nextLabel: "Next: Insights →",
  },
  {
    id: "insights",
    tabMatch: (path) => path.includes("/insights"),
    steps: [
      {
        target: "#tour-canvas-tab-insights",
        placement: "bottom",
        title: "Canvas Insights",
        content: "See AI-generated insights and patterns across your canvas, hypotheses, and discoveries — all in one place.",
      },
    ],
    nextTab: null,
    nextAction: "claude",
  },
  {
    id: "claude",
    type: "modal",
    tabMatch: () => true,
    title: "Do It All with Claude",
    description: "Everything you just explored — canvases, hypotheses, personas, journey maps, roadmaps, and tasks — can be created and managed through natural language conversation using Claude Desktop with the Foundry Co-Founder Plugin.",
    primaryLabel: "Set Up Claude Desktop",
    primaryHref: "/workspace/settings/claude_desktop",
    secondaryLabel: "Maybe later",
    steps: [],
    nextTab: null,
  },
]

const CANVAS_DETAIL_STORAGE_KEY = "foundry_canvas_detail_tour_phase"

const TOUR_CONFIG = {
  canEndTour: true,
  buttons: {
    next: "Next",
    back: "Back",
    done: "Got it",
    end: "Skip",
  },
}

export default class extends Controller {
  static values = {
    name: String,
    completed: Boolean,
  }

  connect() {
    if (this.completedValue || !this.nameValue) return

    // Check if this is a canvas_detail tour resuming on a new tab
    if (this.nameValue === "canvas_detail") {
      this.timeout = setTimeout(() => this.startCanvasDetailTour(), 800)
    } else {
      this.timeout = setTimeout(() => this.startSimpleTour(), 800)
    }
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  // ---- Simple single-page tours ----

  startSimpleTour() {
    const steps = SIMPLE_TOURS[this.nameValue]
    if (!steps) return

    const validSteps = steps
      .filter((step) => !!document.querySelector(step.target))
      .map((step) => ({ ...step, blockElement: true }))

    if (validSteps.length === 0) return

    window.Tourist.show(validSteps, TOUR_CONFIG, {
      onEnd: () => this.completeTour(),
      onSkip: () => this.completeTour(),
    })
  }

  // ---- Multi-phase canvas detail tour ----

  startCanvasDetailTour() {
    const currentPath = window.location.pathname
    const pendingPhaseId = localStorage.getItem(CANVAS_DETAIL_STORAGE_KEY)

    // Find current phase based on URL
    let phase = CANVAS_DETAIL_PHASES.find((p) => p.tabMatch(currentPath))

    // If we have a pending phase from navigation, verify we're on the right tab
    if (pendingPhaseId) {
      const pendingPhase = CANVAS_DETAIL_PHASES.find((p) => p.id === pendingPhaseId)
      if (pendingPhase && pendingPhase.tabMatch(currentPath)) {
        phase = pendingPhase
      }
      localStorage.removeItem(CANVAS_DETAIL_STORAGE_KEY)
    }

    if (!phase) return

    const validSteps = phase.steps
      .filter((step) => !!document.querySelector(step.target))
      .map((step) => ({ ...step, blockElement: true }))

    // If no valid steps on this tab, try to advance to next tab
    if (validSteps.length === 0) {
      if (phase.nextTab) {
        this.navigateToNextTab(phase)
      } else {
        this.completeTour()
      }
      return
    }

    // Customize the "done" button to indicate next tab/phase
    const config = { ...TOUR_CONFIG }
    if (phase.nextTab || phase.nextAction) {
      config.buttons = { ...TOUR_CONFIG.buttons, done: phase.nextLabel || "Next →" }
    }

    window.Tourist.show(validSteps, config, {
      onEnd: () => {
        if (phase.nextTab) {
          this.navigateToNextTab(phase)
        } else if (phase.nextAction) {
          this.advanceToPhase(phase)
        } else {
          this.completeTour()
        }
      },
      onSkip: () => this.completeTour(),
    })
  }

  advanceToPhase(phase) {
    const currentIndex = CANVAS_DETAIL_PHASES.indexOf(phase)
    const nextPhase = CANVAS_DETAIL_PHASES[currentIndex + 1]
    if (!nextPhase) {
      this.completeTour()
      return
    }

    if (nextPhase.type === "modal") {
      // Small delay for Tourist cleanup
      setTimeout(() => this.showPhaseModal(nextPhase), 400)
      return
    }

    // Show the next phase's steps on the same page (no navigation needed)
    const validSteps = nextPhase.steps
      .filter((step) => !!document.querySelector(step.target))
      .map((step) => ({ ...step, blockElement: true }))

    if (validSteps.length === 0) {
      this.completeTour()
      return
    }

    const config = { ...TOUR_CONFIG }
    if (!nextPhase.nextTab && !nextPhase.nextAction) {
      config.buttons = { ...TOUR_CONFIG.buttons, done: "Got it" }
    }

    // Small delay for Tourist cleanup
    setTimeout(() => {
      window.Tourist.show(validSteps, config, {
        onEnd: () => this.completeTour(),
      })
    }, 400)
  }

  showPhaseModal(phase) {
    // Clean up any Tourist instance
    if (window.Tourist && window.Tourist.destroy) {
      window.Tourist.destroy()
    }

    const overlay = document.createElement("div")
    overlay.id = "feature-tour-modal-overlay"
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:999998;display:flex;align-items:center;justify-content:center;"

    const modal = document.createElement("div")
    modal.style.cssText = "background:var(--tourist-bg, #fff);border-radius:0.75rem;padding:2rem;max-width:28rem;width:90%;text-align:center;color:var(--tourist-text, #1f2937);box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);"

    modal.innerHTML = `
      <div style="width:3rem;height:3rem;margin:0 auto 1rem;border-radius:50%;background:#faf9f5;display:flex;align-items:center;justify-content:center;">
        <svg width="24" height="24" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg"><path fill="#d97757" d="M 233.959793 800.214905 L 468.644287 668.536987 L 472.590637 657.100647 L 468.644287 650.738403 L 457.208069 650.738403 L 417.986633 648.322144 L 283.892639 644.69812 L 167.597321 639.865845 L 54.926208 633.825623 L 26.577238 627.785339 L 3.3e-05 592.751709 L 2.73832 575.27533 L 26.577238 559.248352 L 60.724873 562.228149 L 136.187973 567.382629 L 249.422867 575.194763 L 331.570496 580.026978 L 453.261841 592.671082 L 472.590637 592.671082 L 475.328857 584.859009 L 468.724915 580.026978 L 463.570557 575.194763 L 346.389313 495.785217 L 219.543671 411.865906 L 153.100723 363.543762 L 117.181267 339.060425 L 99.060455 316.107361 L 91.248367 266.01355 L 123.865784 230.093994 L 167.677887 233.073853 L 178.872513 236.053772 L 223.248367 270.201477 L 318.040283 343.570496 L 441.825592 434.738342 L 459.946411 449.798706 L 467.194672 444.64447 L 468.080597 441.020203 L 459.946411 427.409485 L 392.617493 305.718323 L 320.778564 181.932983 L 288.80542 130.630859 L 280.348999 99.865845 C 277.369171 87.221436 275.194641 76.590698 275.194641 63.624268 L 312.322174 13.20813 L 332.8591 6.604126 L 382.389313 13.20813 L 403.248352 31.328979 L 434.013519 101.71814 L 483.865753 212.537048 L 561.181274 363.221497 L 583.812134 407.919434 L 595.892639 449.315491 L 600.40271 461.959839 L 608.214783 461.959839 L 608.214783 454.711609 L 614.577271 369.825623 L 626.335632 265.61084 L 637.771851 131.516846 L 641.718201 93.745117 L 660.402832 48.483276 L 697.530334 24.000122 L 726.52356 37.852417 L 750.362549 72 L 747.060486 94.067139 L 732.886047 186.201416 L 705.100708 330.52356 L 686.979919 427.167847 L 697.530334 427.167847 L 709.61084 415.087341 L 758.496704 350.174561 L 840.644348 247.490051 L 876.885925 206.738342 L 919.167847 161.71814 L 946.308838 140.29541 L 997.61084 140.29541 L 1035.38269 196.429626 L 1018.469849 254.416199 L 965.637634 321.422852 L 921.825562 378.201538 L 859.006714 462.765259 L 819.785278 530.41626 L 823.409424 535.812073 L 832.75177 534.92627 L 974.657776 504.724915 L 1051.328979 490.872559 L 1142.818848 475.167786 L 1184.214844 494.496582 L 1188.724854 514.147644 L 1172.456421 554.335693 L 1074.604126 578.496765 L 959.838989 601.449829 L 788.939636 641.879272 L 786.845764 643.409485 L 789.261841 646.389343 L 866.255127 653.637634 L 899.194702 655.409424 L 979.812134 655.409424 L 1129.932861 666.604187 L 1169.154419 692.537109 L 1192.671265 724.268677 L 1188.724854 748.429688 L 1128.322144 779.194641 L 1046.818848 759.865845 L 856.590759 714.604126 L 791.355774 698.335754 L 782.335693 698.335754 L 782.335693 703.731567 L 836.69812 756.885986 L 936.322205 846.845581 L 1061.073975 962.81897 L 1067.436279 991.490112 L 1051.409424 1014.120911 L 1034.496704 1011.704712 L 924.885986 929.234924 L 882.604126 892.107544 L 786.845764 811.48999 L 780.483276 811.48999 L 780.483276 819.946289 L 802.550415 852.241699 L 919.087341 1027.409424 L 925.127625 1081.127686 L 916.671204 1098.604126 L 886.469849 1109.154419 L 853.288696 1103.114136 L 785.073914 1007.355835 L 714.684631 899.516785 L 657.906067 802.872498 L 650.979858 806.81897 L 617.476624 1167.704834 L 601.771851 1186.147705 L 565.530212 1200 L 535.328857 1177.046997 L 519.302124 1139.919556 L 535.328857 1066.550537 L 554.657776 970.792053 L 570.362488 894.68457 L 584.536926 800.134277 L 592.993347 768.724976 L 592.429626 766.630859 L 585.503479 767.516968 L 514.22821 865.369263 L 405.825531 1011.865906 L 320.053711 1103.677979 L 299.516815 1111.812256 L 263.919525 1093.369263 L 267.221497 1060.429688 L 287.114136 1031.114136 L 405.825531 880.107361 L 477.422913 786.52356 L 523.651062 732.483276 L 523.328918 724.671265 L 520.590698 724.671265 L 205.288605 929.395935 L 149.154434 936.644409 L 124.993355 914.01355 L 127.973183 876.885986 L 139.409409 864.80542 L 234.201385 799.570435 L 233.879227 799.8927 Z"/></svg>
      </div>
      <h2 style="font-size:1.25rem;font-weight:700;margin-bottom:0.75rem;">${phase.title}</h2>
      <p style="font-size:0.875rem;color:var(--tourist-text-secondary, #6b7280);margin-bottom:1.5rem;line-height:1.6;">${phase.description}</p>
      <div style="display:flex;gap:0.75rem;justify-content:center;">
        <button id="feature-tour-modal-skip" style="padding:0.5rem 1rem;font-size:0.875rem;color:var(--tourist-text-secondary, #6b7280);background:none;border:1px solid var(--tourist-border, #d1d5db);border-radius:0.375rem;cursor:pointer;">${phase.secondaryLabel}</button>
        <a id="feature-tour-modal-action" href="${phase.primaryHref}" style="padding:0.5rem 1.5rem;font-size:0.875rem;font-weight:600;color:#fff;background:var(--tourist-primary, #2563eb);border:none;border-radius:0.375rem;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:0.375rem;">${phase.primaryLabel} →</a>
      </div>
    `

    overlay.appendChild(modal)
    document.body.appendChild(overlay)

    modal.querySelector("#feature-tour-modal-action").addEventListener("click", () => {
      overlay.remove()
      this.completeTour()
    })

    modal.querySelector("#feature-tour-modal-skip").addEventListener("click", () => {
      overlay.remove()
      this.completeTour()
    })
  }

  navigateToNextTab(phase) {
    const nextTabLink = document.querySelector(phase.nextTab)
    if (!nextTabLink) {
      this.completeTour()
      return
    }

    // Store the next phase ID so we resume after navigation
    const nextPhase = CANVAS_DETAIL_PHASES[CANVAS_DETAIL_PHASES.indexOf(phase) + 1]
    if (nextPhase) {
      localStorage.setItem(CANVAS_DETAIL_STORAGE_KEY, nextPhase.id)
    }

    // Click the tab link to navigate
    nextTabLink.click()
  }

  // ---- Completion ----

  completeTour() {
    this.completedValue = true
    localStorage.removeItem(CANVAS_DETAIL_STORAGE_KEY)

    post("/account/complete_feature_tour", {
      body: JSON.stringify({ name: this.nameValue }),
      contentType: "application/json",
      responseKind: "json",
    })
  }
}
